// Navigation utility for mapping parent menu aliases to routes
export const getNextParentRoute = (currentParentAlias, menuHierarchy) => {
  if (!menuHierarchy || menuHierarchy.length === 0) {
    return null;
  }

  // Get available parents sorted by sort_order
  const availableParents = menuHierarchy
    .filter(menu => menu.status === 1)
    .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

  // Find current parent index
  const currentParentIndex = availableParents.findIndex(menu => menu.alias === currentParentAlias);
  
  // Get next parent
  if (currentParentIndex >= 0 && currentParentIndex < availableParents.length - 1) {
    const nextParent = availableParents[currentParentIndex + 1];

    // Map parent alias to route
    const routeMap = {
      'registration': '/event/registration/basic-detail-form',
      'online_registration_form': '/event/registration/basic-detail-form',
      'event_details': '/event/edit/' + (localStorage.getItem('event_id') || ''),
      'branding': '/event/settings/branding',
      'registration_list': '/event_site/billing-module/manage-orders',
      'settings': '/event/settings/branding',
      'manage': '/event/manage/surveys',
      'survey': '/event/manage/surveys',
      'module': '/event/module/event-module-order',
      'event_site': '/event_site/billing-module/payment-methods'
    };

    // For upload_content, check the first available child instead of going directly to parent
    if (nextParent.alias === 'upload_content') {
      // Use getFirstAvailableSubMenuRoute to find the first available child
      const firstAvailableChildRoute = getFirstAvailableSubMenuRoute('upload_content', menuHierarchy);
      if (firstAvailableChildRoute) {
        return firstAvailableChildRoute;
      }
    }

    // For items_paid, check the first available child instead of going directly to parent
    if (nextParent.alias === 'items_paid') {
      // Use getFirstAvailableSubMenuRoute to find the first available child
      const firstAvailableChildRoute = getFirstAvailableSubMenuRoute('items_paid', menuHierarchy);
      if (firstAvailableChildRoute) {
        return firstAvailableChildRoute;
      }
    }
    
    const route = routeMap[nextParent.alias] || null;
    
    return route;
  }
  
  // If no more parents, navigate to preview
  if (currentParentIndex >= 0 && currentParentIndex === availableParents.length - 1) {
    return '/event/preview';
  }
  
  return null;
};

// New utility function to get the first available sub-menu route for parent menus
export const getFirstAvailableSubMenuRoute = (parentAlias, menuHierarchy) => {
  if (!menuHierarchy || menuHierarchy.length === 0) {
    // Fallback to default routes when menu hierarchy is not loaded
    const defaultRouteMap = {
      'event_details': '/event/edit/' + (localStorage.getItem('event_id') || ''),
      'branding': '/event/settings/branding',
      'items_paid': '/event_site/billing-module/payment-methods',
      'online_registration_form': '/event/registration/basic-detail-form',
      'upload_content': '/event/module/event-module-order',
      'survey': '/event/manage/surveys'
    };
    return defaultRouteMap[parentAlias] || null;
  }

  // Find the parent menu
  const parentMenu = menuHierarchy.find(menu => menu.alias === parentAlias);
  if (!parentMenu || !parentMenu.children || parentMenu.children.length === 0) {
    // No children, return parent's own route
    const parentRouteMap = {
      'event_details': '/event/edit/' + (localStorage.getItem('event_id') || ''),
      'branding': '/event/settings/branding',
      'items_paid': '/event_site/billing-module/payment-methods',
      'online_registration_form': '/event/registration/basic-detail-form',
      'upload_content': '/event/module/event-module-order',
      'survey': '/event/manage/surveys'
    };
    return parentRouteMap[parentAlias] || null;
  }

  // Get available children sorted by sort_order (only those with status === 1)
  const availableChildren = parentMenu.children
    .filter(child => Number(child.status) === 1)
    .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

  if (availableChildren.length === 0) {
    // No available children, return parent's own route
    const parentRouteMap = {
      'event_details': '/event/edit/' + (localStorage.getItem('event_id') || ''),
      'branding': '/event/settings/branding',
      'items_paid': '/event_site/billing-module/payment-methods',
      'online_registration_form': '/event/registration/basic-detail-form',
      'upload_content': '/event/module/event-module-order',
      'survey': '/event/manage/surveys'
    };
    return parentRouteMap[parentAlias] || null;
  }

  // Get the first available child and map it to its route
  const firstAvailableChild = availableChildren[0];

  // Use the comprehensive route mappings that already exist in this file
  const childRouteMap = {
    // Event Details children
    'event_setup': '/event/edit/' + (localStorage.getItem('event_id') || ''),
    'event_details': '/event/edit/' + (localStorage.getItem('event_id') || ''),
    'date_location': '/event/edit/' + (localStorage.getItem('event_id') || ''),

    // Branding children
    'branding': '/event/settings/branding',
    'reg_site_header_and_footerex': '/event/settings/branding',
    'opening-hour': '/event/settings/opening-hour',
    'contact-person': '/event/settings/contact-person',
    'registration-setting': '/event/settings/registration-setting',
    'eventsite-banners': '/event/settings/eventsite-banners',
    'website-sections': '/event/settings/website-sections',
    'custom_sections': '/event/settings/custom-sections',

    // Items Paid children
    'payment_methods': '/event_site/billing-module/payment-methods',
    'payment_providers': '/event_site/billing-module/payment-providers',
    'items': '/event_site/billing-module/items',
    'voucher': '/event_site/billing-module/voucher',
    'purchase_policy': '/event_site/billing-module/purchase-policy',
    'orders': '/event_site/billing-module/manage-orders',
    'registration_form_stats': '/event_site/billing-module/registration_form/statsdashboard',


    'waiting_list_orders': '/event_site/billing-module/waiting-list-orders',

    // Online Registration children
    'attendee_fields': '/event/registration/basic-detail-form',
    'private_address_fields': '/event/registration/private-address-detail-form',
    'attendee_types': '/event/registration/attendee-type-form',
    'custom_fields': '/event/registration/custom-fields',
    'sub_registrations': '/event/registration/sub-registration',
    'accommodation': '/event/registration/manage/hotels',
    'gdpr_policy': '/event/registration/gdpr',
    'terms_conditions': '/event/registration/tos',
    'add_attendee_to_registration': '/event/registration/add-attendee-to-registration',

    // Upload Content children - using existing comprehensive mappings
    'customize_app_menus': '/event/module/event-module-order',
    'customize_website_menus': '/event/module/eventsite-module-order',
    'attendees': '/event/module/attendees',
    'agendas': '/event/module/programs',
    'speakers': '/event/module/speakers',
    'infobooth': '/event/module/practical-information',
    'additional_info': '/event/module/additional-information',
    'general_info': '/event/module/general-information',
    'maps': '/event/module/map',
    'subregistration': '/event/module/sub-registration',
    'ddirectory': '/event/module/documents',

    // Additional mappings from existing navigation utilities
    'practical-info': '/event/module/practical-information',
    'additional-info': '/event/module/additional-information',
    'general-info': '/event/module/general-information',
    'information_pages': '/event/module/information-pages',
    'directory': '/event/module/documents'
  };

  const route = childRouteMap[firstAvailableChild.alias];


  if (!route) {
  }

  return route || null;
};

// Enhanced function to check if next menu item is available (child or parent)
export const isNextMenuItemAvailable = (menuHierarchy, currentStep) => {
  if (!menuHierarchy || menuHierarchy.length === 0) {
    // Fallback to default navigation
    if (currentStep === 1) return true;
    if (currentStep === 2) return true;
    if (currentStep === 3) return false; // No more steps in default navigation
    return false;
  }

  // Find the event_details parent menu
  const eventDetailsMenu = menuHierarchy.find(menu => menu.alias === 'event_details');
  if (!eventDetailsMenu || !eventDetailsMenu.children) {
    return false;
  }

  // Get available children sorted by sort_order
  const availableChildren = eventDetailsMenu.children
    .filter(child => child.status === 1)
    .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

  // Find current step index
  const currentStepIndex = availableChildren.findIndex(child => {
    // Map step numbers to aliases
    if (currentStep === 1 && child.alias === 'event_setup') return true;
    if (currentStep === 2 && child.alias === 'event_details') return true;
    if (currentStep === 3 && child.alias === 'date_location') return true;
    return false;
  });

  // Check if there's a next child available
  if (currentStepIndex >= 0 && currentStepIndex < availableChildren.length - 1) {
    return true; // Next child available
  }

  // If no next child, check if there's a next parent available
  const availableParents = menuHierarchy
    .filter(menu => menu.status === 1)
    .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

  const currentParentIndex = availableParents.findIndex(menu => menu.alias === 'event_details');
  
  if (currentParentIndex >= 0 && currentParentIndex < availableParents.length - 1) {
    return true; // Next parent available
  }

  return false; // No next menu item available
};

// Navigation utility for online_registration_form parent menu
export const getNextRegistrationNavigation = (currentChildAlias, menuHierarchy) => {
  if (!menuHierarchy || menuHierarchy.length === 0) {
    return null;
  }

  // Find the online_registration_form parent menu
  const registrationMenu = menuHierarchy.find(menu => menu.alias === 'online_registration_form');
  
  if (!registrationMenu || !registrationMenu.children) {
    return null;
  }

  // Get available children sorted by sort_order (only those with status === 1)
  const availableChildren = registrationMenu.children
    .filter(child => child.status === 1)
    .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

  // Find current child index
  const currentChildIndex = availableChildren.findIndex(child => child.alias === currentChildAlias);
  // Find next available child within current parent
  if (currentChildIndex >= 0 && currentChildIndex < availableChildren.length - 1) {
    const nextChild = availableChildren[currentChildIndex + 1];
    // Check if next child is terms_conditions and if it's disabled/not available
    if (nextChild.alias === 'add_attendee_to_registration') {
      // Check if terms_conditions is actually available (status === 1)
      const termsConditionsChild = registrationMenu.children.find(child => child.alias === 'add_attendee_to_registration');
      if (!termsConditionsChild || termsConditionsChild.status !== 1) {
        // Continue to parent navigation logic instead of navigating to terms_conditions
      } else {
        // terms_conditions is available, navigate to it
        const route = '/event/registration/add-attendee-to-registration';
        return {
          step: route,
          name: nextChild.name,
          alias: nextChild.alias,
          type: 'child',
          route: route
        };
      }
    } else {
      // Map child alias to route for other children
      const childRouteMap = {
        'attendee_fields': '/event/registration/basic-detail-form',
        'private_address_fields': '/event/registration/private-address-detail-form',
        'attendee_types': '/event/registration/attendee-type-form',
        'custom_fields': '/event/registration/custom-fields',
        'company_fields': '/event/registration/company-detail-form',
        'sub_registrations': '/event/registration/sub-registration',
        'accommodation': '/event/registration/manage/hotels',
        'gdpr_policy': '/event/registration/gdpr',
        'terms_conditions': '/event/registration/tos',
        'add_attendee_to_registration': '/event/registration/add-attendee-to-registration'
      };
      
      const route = childRouteMap[nextChild.alias];
      
      return {
        step: route,
        name: nextChild.name,
        alias: nextChild.alias,
        type: 'child',
        route: route
      };
    }
  }

  // If no next child, check for next parent
  const availableParents = menuHierarchy
    .filter(menu => menu.status === 1)
    .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

  const currentParentIndex = availableParents.findIndex(menu => menu.alias === 'online_registration_form');
  
  if (currentParentIndex >= 0 && currentParentIndex < availableParents.length - 1) {
    const nextParent = availableParents[currentParentIndex + 1];
    // Map parent alias to route
    const routeMap = {
      'registration': '/event/registration/basic-detail-form',
      'online_registration_form': '/event/registration/basic-detail-form',
      'event_details': '/event/edit/' + (localStorage.getItem('event_id') || ''),
      'branding': '/event/settings/branding',
      'registration_list': '/event_site/billing-module/manage-orders',
      'settings': '/event/settings/branding',
      'manage': '/event/manage/surveys',
      'survey': '/event/manage/surveys',
      'module': '/event/module/event-module-order',
      'event_site': '/event_site/billing-module/payment-methods'
    };

    // For upload_content, check the first available child instead of going directly to parent
    if (nextParent.alias === 'upload_content') {
      // Use getFirstAvailableSubMenuRoute to find the first available child
      const firstAvailableChildRoute = getFirstAvailableSubMenuRoute('upload_content', menuHierarchy);
      if (firstAvailableChildRoute) {
        return {
          step: firstAvailableChildRoute,
          name: nextParent.name,
          alias: nextParent.alias,
          type: 'parent',
          route: firstAvailableChildRoute
        };
      }
    }

    // For items_paid, check the first available child instead of going directly to parent
    if (nextParent.alias === 'items_paid') {
      // Use getFirstAvailableSubMenuRoute to find the first available child
      const firstAvailableChildRoute = getFirstAvailableSubMenuRoute('items_paid', menuHierarchy);
      if (firstAvailableChildRoute) {
        return {
          step: firstAvailableChildRoute,
          name: nextParent.name,
          alias: nextParent.alias,
          type: 'parent',
          route: firstAvailableChildRoute
        };
      }
    }
    
    const route = routeMap[nextParent.alias];
    
    return {
      step: route,
      name: nextParent.name,
      alias: nextParent.alias,
      type: 'parent',
      route: route
    };
  }

  return null;
};

// Enhanced function to check if next registration menu item is available (child or parent)
export const isNextRegistrationMenuItemAvailable = (currentChildAlias, menuHierarchy) => {
  if (!menuHierarchy || menuHierarchy.length === 0) {
    return false;
  }

  // Find the online_registration_form parent menu
  const registrationMenu = menuHierarchy.find(menu => menu.alias === 'online_registration_form');
  
  if (!registrationMenu || !registrationMenu.children) {
    return false;
  }

  // Get available children sorted by sort_order
  const availableChildren = registrationMenu.children
    .filter(child => child.status === 1)
    .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

  // Find current child index
  const currentChildIndex = availableChildren.findIndex(child => child.alias === currentChildAlias);

  // Check if there's a next child available
  if (currentChildIndex >= 0 && currentChildIndex < availableChildren.length - 1) {
    return true; // Next child available
  }

  // If no next child, check if there's a next parent available
  const availableParents = menuHierarchy
    .filter(menu => menu.status === 1)
    .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

  const currentParentIndex = availableParents.findIndex(menu => menu.alias === 'online_registration_form');
  
  if (currentParentIndex >= 0 && currentParentIndex < availableParents.length - 1) {
    return true; // Next parent available
  }

  return false; // No next menu item available
}; 

// Navigation utility for items_paid parent menu (billing module)
export const getNextBillingNavigation = (currentChildAlias, menuHierarchy) => {
  if (!menuHierarchy || menuHierarchy.length === 0) {
    return null;
  }

  // Find the items_paid parent menu
  const billingMenu = menuHierarchy.find(menu => menu.alias === 'items_paid');
  
  if (!billingMenu || !billingMenu.children) {
    return null;
  }

  // Get available children sorted by sort_order (only those with status === 1)
  const availableChildren = billingMenu.children
    .filter(child => child.status === 1)
    .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

  // Find current child index
  const currentChildIndex = availableChildren.findIndex(child => child.alias === currentChildAlias);
  // Find next available child within current parent
  if (currentChildIndex >= 0 && currentChildIndex < availableChildren.length - 1) {
    const nextChild = availableChildren[currentChildIndex + 1];
    // Map child alias to route
    const childRouteMap = {
      'payment_methods': '/event_site/billing-module/payment-methods',
      'payment_providers': '/event_site/billing-module/payment-providers',
      'public': '/event_site/billing-module/ean-invoice',
      'fik': '/event_site/billing-module/fik-setting',
      'items': '/event_site/billing-module/items',
      'voucher': '/event_site/billing-module/voucher',
      'purchase_policy': '/event_site/billing-module/purchase-policy',
      'orders': '/event_site/billing-module/manage-orders',
      'registration_form_stats': '/event_site/billing-module/registration_form/statsdashboard',

      'waiting_list_orders': '/event_site/billing-module/waiting-list-orders'
    };
    
    const route = childRouteMap[nextChild.alias];
    
    return {
      step: route,
      name: nextChild.name,
      alias: nextChild.alias,
      type: 'child',
      route: route
    };
  }

  // If no next child, check for next parent
  const availableParents = menuHierarchy
    .filter(menu => menu.status === 1)
    .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

  const currentParentIndex = availableParents.findIndex(menu => menu.alias === 'items_paid');
  
  if (currentParentIndex >= 0 && currentParentIndex < availableParents.length - 1) {
    const nextParent = availableParents[currentParentIndex + 1];
    // Map parent alias to route
    const routeMap = {
      'registration': '/event/registration/basic-detail-form',
      'online_registration_form': '/event/registration/basic-detail-form',
      'event_details': '/event/edit/' + (localStorage.getItem('event_id') || ''),
      'branding': '/event/settings/branding',
      'registration_list': '/event_site/billing-module/manage-orders',
      'settings': '/event/settings/branding',
      'manage': '/event/manage/surveys',
      'survey': '/event/manage/surveys',
      'module': '/event/module/event-module-order',
      'upload_content': '/event/module/event-module-order',
      'items_paid': '/event_site/billing-module/payment-methods',
      'event_site': '/event_site/billing-module/payment-methods'
    };
    
    const route = routeMap[nextParent.alias];
    
    return {
      step: route,
      name: nextParent.name,
      alias: nextParent.alias,
      type: 'parent',
      route: route
    };
  }

  return null;
};

// Enhanced function to check if next billing menu item is available (child or parent)
export const isNextBillingMenuItemAvailable = (currentChildAlias, menuHierarchy) => {
  if (!menuHierarchy || menuHierarchy.length === 0) {
    return false;
  }

  // Find the items_paid parent menu
  const billingMenu = menuHierarchy.find(menu => menu.alias === 'items_paid');
  
  if (!billingMenu || !billingMenu.children) {
    return false;
  }

  // Get available children sorted by sort_order
  const availableChildren = billingMenu.children
    .filter(child => child.status === 1)
    .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

  // Find current child index
  const currentChildIndex = availableChildren.findIndex(child => child.alias === currentChildAlias);

  // Check if there's a next child available
  if (currentChildIndex >= 0 && currentChildIndex < availableChildren.length - 1) {
    return true; // Next child available
  }

  // If no next child, check if there's a next parent available
  const availableParents = menuHierarchy
    .filter(menu => menu.status === 1)
    .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

  const currentParentIndex = availableParents.findIndex(menu => menu.alias === 'items_paid');
  
  if (currentParentIndex >= 0 && currentParentIndex < availableParents.length - 1) {
    return true; // Next parent available
  }

  return false; // No next menu item available
}; 

// Navigation utility for upload_content parent menu
export const getNextUploadContentNavigation = (currentChildAlias, menuHierarchy) => {
    if (!menuHierarchy || menuHierarchy.length === 0) {
        return null;
    }

    // Find the upload_content parent menu
    const uploadContentMenu = menuHierarchy.find(menu => menu.alias === 'upload_content');

    if (!uploadContentMenu || !uploadContentMenu.children) {
        return null;
    }

    // Get available children sorted by sort_order (only those with status === 1)
    const availableChildren = uploadContentMenu.children
        .filter(child => child.status === 1)
        .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

    // Find current child index
    const currentChildIndex = availableChildren.findIndex(child => child.alias === currentChildAlias);
    // Find next available child within current parent
    if (currentChildIndex >= 0 && currentChildIndex < availableChildren.length - 1) {
        const nextChild = availableChildren[currentChildIndex + 1];
        // Map child alias to route
        const childRouteMap = {
          'customize_app_menus': '/event/module/event-module-order',
            'customize_website_menus': '/event/module/eventsite-module-order',
            'agendas': '/event/module/programs',
            'attendees': '/event/module/attendees',
            'speakers': '/event/module/speakers',
            'directory': '/event/module/documents',
            'practical-info': '/event/module/practical-information',
            'maps': '/event/module/map',
            'subregistration': '/event/module/sub-registration',
            'additional-info': '/event/module/additional-information',
            'general-info': '/event/module/general-information',
            'information_pages': '/event/module/information-pages'
        };

        const route = childRouteMap[nextChild.alias];

        return {
            step: route,
            name: nextChild.name,
            alias: nextChild.alias,
            type: 'child',
            route: route
        };
    }

    // If no next child, check for next parent
    const availableParents = menuHierarchy
        .filter(menu => menu.status === 1)
        .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

    const currentParentIndex = availableParents.findIndex(menu => menu.alias === 'upload_content');

    if (currentParentIndex >= 0 && currentParentIndex < availableParents.length - 1) {
        const nextParent = availableParents[currentParentIndex + 1];
        // Map parent alias to route
        const routeMap = {
            'registration': '/event/registration/basic-detail-form',
            'online_registration_form': '/event/registration/basic-detail-form',
            'event_details': '/event/edit/' + (localStorage.getItem('event_id') || ''),
            'branding': '/event/settings/branding',
            'registration_list': '/event_site/billing-module/manage-orders',
            'settings': '/event/settings/branding',
            'manage': '/event/manage/surveys',
            'survey': '/event/manage/surveys',
            'module': '/event/module/event-module-order',
            'upload_content': '/event/module/event-module-order',
            'items_paid': '/event_site/billing-module/payment-methods',
            'event_site': '/event_site/billing-module/payment-methods'
        };

        const route = routeMap[nextParent.alias];

        return {
            step: route,
            name: nextParent.name,
            alias: nextParent.alias,
            type: 'parent',
            route: route
        };
    }

    return null;
}; 

// Utility function to get next available unlocked child for upload content components
// Usage in components:
// import { getNextUnlockedChild } from '../../utils/navigationUtils';
// 
// // In save method or navigation logic:
// getNextUnlockedChild('current-child-alias', this.state.menuHierarchy).then(nextChild => {
//     if (nextChild && nextChild.route) {
//         this.props.history.push(nextChild.route);
//     } else {
//         // Fallback to original navigation
//         this.props.history.push(this.state.next);
//     }
// });
export const getNextUnlockedChild = async (currentChildAlias, menuHierarchy) => {
  if (!menuHierarchy || menuHierarchy.length === 0) {
    return null;
  }

  // Get event ID from localStorage
  const eventId = localStorage.getItem('event_id');
  if (!eventId) {
    return null;
  }

  // Fetch current modules data from API
  let modules = null;
  try {
    const { service } = await import("../services/service");
    const response = await service.post(`${process.env.REACT_APP_URL}/event-settings/modules`, { event_id: eventId });

    if (response.success && response.data && response.data.modules) {
      modules = response.data.modules;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }

  // Alias mapping for different naming conventions
  const aliasMapping = {
    'directory': 'ddirectory',
    'practical-info': 'infobooth',
    'additional-info': 'additional_info',
    'general-info': 'general_info'
  };

  // Find the upload_content parent menu
  const uploadContentMenu = menuHierarchy.find(menu => menu.alias === 'upload_content');

  if (!uploadContentMenu || !uploadContentMenu.children) {
    return null;
  }

  // Get all children sorted by sort_order
  const allChildren = uploadContentMenu.children.sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

  // Find current child index
  const currentChildIndex = allChildren.findIndex(child => child.alias === currentChildAlias);

  if (currentChildIndex === -1) {
    return null;
  }

  // Check if current route is locked and automatically redirect to first available unlocked child
  const currentMappedAlias = aliasMapping[currentChildAlias] || currentChildAlias;
  const currentCorrespondingModule = modules.find(module => module.alias === currentMappedAlias);

  let isCurrentLocked = false;

  if (currentCorrespondingModule) {
    isCurrentLocked = Number(currentCorrespondingModule.status) === 0;
  } else {
    // Check if it's a special menu item that doesn't need a module
    const alwaysAvailableMenuItems = [
      'customize_website_menus',
      'customize_app_menus',
      'information_pages'
    ];
    isCurrentLocked = !alwaysAvailableMenuItems.includes(currentChildAlias);
  }

  if (isCurrentLocked) {

    // Find the next available unlocked child after current position
    for (let i = currentChildIndex + 1; i < allChildren.length; i++) {
      const potentialFirstChild = allChildren[i];
      const mappedAlias = aliasMapping[potentialFirstChild.alias] || potentialFirstChild.alias;
      const correspondingModule = modules.find(module => module.alias === mappedAlias);

      if (correspondingModule) {
        const isLocked = Number(correspondingModule.status) === 0;
        if (!isLocked) {
          // Found first unlocked child, return it
          const childRouteMap = {
            'customize_app_menus': '/event/module/event-module-order',
            'customize_website_menus': '/event/module/eventsite-module-order',
            'agendas': '/event/module/programs',
            'attendees': '/event/module/attendees',
            'speakers': '/event/module/speakers',
            'directory': '/event/module/documents',
            'practical-info': '/event/module/practical-information',
            'maps': '/event/module/map',
            'subregistration': '/event/module/sub-registration',
            'additional-info': '/event/module/additional-information',
            'general-info': '/event/module/general-information',
            'information_pages': '/event/module/information-pages',
            'team': '/event/module/teams',
            'news': '/event/module/news'
          };

          const route = childRouteMap[potentialFirstChild.alias];
          if (route) {
            return {
              step: route,
              name: potentialFirstChild.name,
              alias: potentialFirstChild.alias,
              type: 'child',
              route: route,
              wasSkipped: true,
              wasRedirectedFromLocked: true
            };
          }
        }
      } else {
        // Check if it's a special menu item that doesn't need a module
        const alwaysAvailableMenuItems = [
          'customize_website_menus',
          'customize_app_menus',
          'information_pages'
        ];

        if (alwaysAvailableMenuItems.includes(potentialFirstChild.alias)) {
          // Special check for information_pages - should follow general_info lock status
          if (potentialFirstChild.alias === 'information_pages') {
            const generalInfoModule = modules.find(module => module.alias === 'general_info');
            if (generalInfoModule && Number(generalInfoModule.status) === 0) {
              // Skip information_pages if general_info is locked
              continue;
            }
          }

          const childRouteMap = {
            'customize_app_menus': '/event/module/event-module-order',
            'customize_website_menus': '/event/module/eventsite-module-order',
            'information_pages': '/event/module/information-pages'
          };

          const route = childRouteMap[potentialFirstChild.alias];
          if (route) {
            return {
              step: route,
              name: potentialFirstChild.name,
              alias: potentialFirstChild.alias,
              type: 'child',
              route: route,
              wasSkipped: true,
              wasRedirectedFromLocked: true
            };
          }
        }
      }
    }


    // If no next child found, try to navigate to next available parent
    const availableParents = menuHierarchy
      .filter(menu => menu.status === 1)
      .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

    const currentParentIndex = availableParents.findIndex(menu => menu.alias === 'upload_content');

    if (currentParentIndex >= 0 && currentParentIndex < availableParents.length - 1) {
      const nextParent = availableParents[currentParentIndex + 1];

      // Map parent alias to route
      const routeMap = {
        'registration': '/event/registration/basic-detail-form',
        'online_registration_form': '/event/registration/basic-detail-form',
        'event_details': '/event/edit/' + (localStorage.getItem('event_id') || ''),
        'branding': '/event/settings/branding',
        'registration_list': '/event_site/billing-module/manage-orders',
        'settings': '/event/settings/branding',
        'manage': '/event/manage/surveys',
        'survey': '/event/manage/surveys',
        'module': '/event/module/event-module-order',
        'upload_content': '/event/module/event-module-order',
        'items_paid': '/event_site/billing-module/payment-methods',
        'event_site': '/event_site/billing-module/payment-methods'
      };

      const route = routeMap[nextParent.alias];
      if (route) {
        return {
          step: route,
          name: nextParent.name,
          alias: nextParent.alias,
          type: 'parent',
          route: route,
          wasSkipped: false,
          wasRedirectedFromLocked: true
        };
      }
    }

    return null;
  }

  // Start looking for the next available (unlocked) child
  let nextChildIndex = currentChildIndex + 1;

  // Keep looking until we find an unlocked child or run out of children
  while (nextChildIndex < allChildren.length) {
    const potentialNextChild = allChildren[nextChildIndex];

    // Check if this child is locked by finding its corresponding module
    const mappedAlias = aliasMapping[potentialNextChild.alias] || potentialNextChild.alias;
    const correspondingModule = modules.find(module => module.alias === mappedAlias);

    if (correspondingModule) {
      const isLocked = Number(correspondingModule.status) === 0;

      if (!isLocked) {
        // Found an unlocked child, return it with route mapping

        // Map child alias to route
        const childRouteMap = {
          'customize_app_menus': '/event/module/event-module-order',
          'customize_website_menus': '/event/module/eventsite-module-order',
          'agendas': '/event/module/programs',
          'attendees': '/event/module/attendees',
          'speakers': '/event/module/speakers',
          'directory': '/event/module/documents',
          'practical-info': '/event/module/practical-information',
          'maps': '/event/module/map',
          'subregistration': '/event/module/sub-registration',
          'additional-info': '/event/module/additional-information',
          'general-info': '/event/module/general-information',
          'information_pages': '/event/module/information-pages'
        };

        const route = childRouteMap[potentialNextChild.alias];
        if (route) {
          return {
            step: route,
            name: potentialNextChild.name,
            alias: potentialNextChild.alias,
            type: 'child',
            route: route,
            wasSkipped: nextChildIndex > currentChildIndex + 1
          };
        }
        break;
      } else {
        nextChildIndex++;
      }
    } else {
      // If no corresponding module found, check if it's a special menu item that doesn't need a module
      // These are menu items that are always available for navigation
      const alwaysAvailableMenuItems = [
        'customize_website_menus',  // This is a valid navigation target
        'customize_app_menus',      // This is also a valid navigation target
        'information_pages'         // This is also always available
      ];

      if (alwaysAvailableMenuItems.includes(potentialNextChild.alias)) {
        // Special check for information_pages - should follow general_info lock status
        if (potentialNextChild.alias === 'information_pages') {
          const generalInfoModule = modules.find(module => module.alias === 'general_info');
          if (generalInfoModule && Number(generalInfoModule.status) === 0) {
            nextChildIndex++;
            continue;
          }
        }

        // Map child alias to route
        const childRouteMap = {
          'customize_app_menus': '/event/module/event-module-order',
          'customize_website_menus': '/event/module/eventsite-module-order',
          'agendas': '/event/module/programs',
          'attendees': '/event/module/attendees',
          'speakers': '/event/module/speakers',
          'directory': '/event/module/documents',
          'practical-info': '/event/module/practical-information',
          'maps': '/event/module/map',
          'subregistration': '/event/module/sub-registration',
          'additional-info': '/event/module/additional-information',
          'general-info': '/event/module/general-information',
          'information_pages': '/event/module/information-pages'
        };

        const route = childRouteMap[potentialNextChild.alias];
        if (route) {
          return {
            step: route,
            name: potentialNextChild.name,
            alias: potentialNextChild.alias,
            type: 'child',
            route: route,
            wasSkipped: nextChildIndex > currentChildIndex + 1
          };
        }
        break;
      } else {
        // For other menu items without modules, treat as locked for safety
        nextChildIndex++;
      }
    }
  }


  // If no next child found, try to navigate to next available parent
  const availableParents = menuHierarchy
    .filter(menu => menu.status === 1)
    .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

  const currentParentIndex = availableParents.findIndex(menu => menu.alias === 'upload_content');

  if (currentParentIndex >= 0 && currentParentIndex < availableParents.length - 1) {
    const nextParent = availableParents[currentParentIndex + 1];

    // Map parent alias to route
    const routeMap = {
      'registration': '/event/registration/basic-detail-form',
      'online_registration_form': '/event/registration/basic-detail-form',
      'event_details': '/event/edit/' + (localStorage.getItem('event_id') || ''),
      'branding': '/event/settings/branding',
      'registration_list': '/event_site/billing-module/manage-orders',
      'settings': '/event/settings/branding',
      'manage': '/event/manage/surveys',
      'survey': '/event/manage/surveys',
      'module': '/event/module/event-module-order',
      'upload_content': '/event/module/event-module-order',
      'items_paid': '/event_site/billing-module/payment-methods',
      'event_site': '/event_site/billing-module/payment-methods'
    };

    const route = routeMap[nextParent.alias];
    if (route) {
      return {
        step: route,
        name: nextParent.name,
        alias: nextParent.alias,
        type: 'parent',
        route: route,
        wasSkipped: false
      };
    }
  }

  return null;
};

/**
 * Global utility function for items_paid module navigation
 * Skips locked children and navigates to the next available (unlocked) child
 * @param {string} currentChildAlias - The alias of the current child component
 * @param {Array} menuHierarchy - The complete menu hierarchy data
 * @param {Object} event - The event object containing lock status information
 * @returns {string|null} - The route to navigate to, or null if no route found
 */
export const getNextItemsPaidNavigation = (currentChildAlias, menuHierarchy, event) => {
  if (!menuHierarchy || !event) {
    return null;
  }

  try {
    // Find the items_paid parent menu
    const itemsPaidMenu = menuHierarchy.find(menu => menu.alias === 'items_paid');
    if (!itemsPaidMenu || !itemsPaidMenu.children) {
      return null;
    }

    // Get all children sorted by sort_order
    const allChildren = itemsPaidMenu.children.sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));
    // Find current child index
    const currentChildIndex = allChildren.findIndex(child => child.alias === currentChildAlias);
    if (currentChildIndex === -1) {
      return null;
    }

    // Check if current route is locked and automatically redirect to first available unlocked child
    let isCurrentLocked = false;

    if (currentChildAlias === 'payment_providers') {
      // Payment providers is locked if credit_card_payment status is 0
      isCurrentLocked = !(event.eventsite_secion_fields &&
        Number(event.eventsite_secion_fields.company_detail.credit_card_payment.status) === 1);
    } else if (currentChildAlias === 'ean_invoice' || currentChildAlias === 'company_public_payment' || currentChildAlias === 'public' || currentChildAlias === 'ean_setting') {
      // EAN invoice is locked if company_public_payment status is 0
      isCurrentLocked = !(event.eventsite_secion_fields &&
        Number(event.eventsite_secion_fields.company_detail.company_public_payment.status) === 1);
    } else if (currentChildAlias === 'fik_setting' || currentChildAlias === 'fik') {
      // FIK setting is locked if eventsite_billing_fik is 0
      isCurrentLocked = !(event.eventsite_payment_setting &&
        Number(event.eventsite_payment_setting.eventsite_billing_fik) === 1);
    } else {
      // For other items, check if they are available using menu hierarchy status
      isCurrentLocked = allChildren[currentChildIndex].status === 0;
    }

    if (isCurrentLocked) {
      // Find the first available unlocked child from the beginning
      for (let i = 0; i < allChildren.length; i++) {
        const potentialFirstChild = allChildren[i];
        let isLocked = false;

        if (potentialFirstChild.alias === 'payment_providers') {
          // Payment providers is locked if credit_card_payment status is 0
          isLocked = !(event.eventsite_secion_fields &&
            Number(event.eventsite_secion_fields.company_detail.credit_card_payment.status) === 1);
        } else if (potentialFirstChild.alias === 'ean_invoice' || potentialFirstChild.alias === 'company_public_payment' || potentialFirstChild.alias === 'public' || potentialFirstChild.alias === 'ean_setting') {
          // EAN invoice is locked if company_public_payment status is 0
          isLocked = !(event.eventsite_secion_fields &&
            Number(event.eventsite_secion_fields.company_detail.company_public_payment.status) === 1);
        } else if (potentialFirstChild.alias === 'fik_setting' || potentialFirstChild.alias === 'fik') {
          // FIK setting is locked if eventsite_billing_fik is 0
          isLocked = !(event.eventsite_payment_setting &&
            Number(event.eventsite_payment_setting.eventsite_billing_fik) === 1);
        } else {
          // For other items, check if they are available using menu hierarchy status
          isLocked = potentialFirstChild.status === 0;
        }

        if (!isLocked) {
          // Found first unlocked child, return it
          const childRouteMap = {
            'payment_methods': '/event_site/billing-module/payment-methods',
            'payment_providers': '/event_site/billing-module/payment-providers',
            'public': '/event_site/billing-module/ean-invoice',
            'ean_invoice': '/event_site/billing-module/ean-invoice',
            'company_public_payment': '/event_site/billing-module/ean-invoice',
            'ean_setting': '/event_site/billing-module/ean-invoice',
            'fik_setting': '/event_site/billing-module/fik-setting',
            'fik': '/event_site/billing-module/fik-setting',
            'items': '/event_site/billing-module/items',
            'voucher': '/event_site/billing-module/voucher',
            'purchase_policy': '/event_site/billing-module/purchase-policy',
            'orders': '/event_site/billing-module/manage-orders',
            'registration_form_stats': '/event_site/billing-module/registration_form/statsdashboard',

            'waiting_list_orders': '/event_site/billing-module/waiting-list-orders'
          };

          const route = childRouteMap[potentialFirstChild.alias];
          if (route) {
            // Automatically navigate to the first unlocked child
            window.location.href = route;
            return {
              step: route,
              name: potentialFirstChild.name,
              alias: potentialFirstChild.alias,
              type: 'child',
              route: route,
              wasSkipped: true,
              wasRedirectedFromLocked: true
            };
          }
        }
      }

      return null;
    }

    // Start looking for the next available (unlocked) child
    let nextChildIndex = currentChildIndex + 1;

    // Keep looking until we find an unlocked child or run out of children
    while (nextChildIndex < allChildren.length) {
      const potentialNextChild = allChildren[nextChildIndex];
      // Check if this child is locked using the correct logic from ItemsPaidSection.jsx
      let isLocked = false;

      if (potentialNextChild.alias === 'payment_providers') {
        // Payment providers is locked if credit_card_payment status is 0
        isLocked = !(event.eventsite_secion_fields &&
          Number(event.eventsite_secion_fields.company_detail.credit_card_payment.status) === 1);
      } else if (potentialNextChild.alias === 'ean_invoice' || potentialNextChild.alias === 'company_public_payment' || potentialNextChild.alias === 'public' || potentialNextChild.alias === 'ean_setting') {
        // EAN invoice is locked if company_public_payment status is 0
        isLocked = !(event.eventsite_secion_fields &&
          Number(event.eventsite_secion_fields.company_detail.company_public_payment.status) === 1);
      } else if (potentialNextChild.alias === 'fik_setting' || potentialNextChild.alias === 'fik') {
        // FIK setting is locked if eventsite_billing_fik is 0
        isLocked = !(event.eventsite_payment_setting &&
          Number(event.eventsite_payment_setting.eventsite_billing_fik) === 1);
      } else {
        // For other items, check if they are available using menu hierarchy status
        isLocked = potentialNextChild.status === 0;
      }

      if (!isLocked) {
        // Found an unlocked child, return its route
        // Map child alias to route
        const childRouteMap = {
          'payment_methods': '/event_site/billing-module/payment-methods',
          'payment_providers': '/event_site/billing-module/payment-providers',
          'public': '/event_site/billing-module/ean-invoice',
          'ean_invoice': '/event_site/billing-module/ean-invoice',
          'company_public_payment': '/event_site/billing-module/ean-invoice',
          'ean_setting': '/event_site/billing-module/ean-invoice',
          'fik_setting': '/event_site/billing-module/fik-setting',
          'fik': '/event_site/billing-module/fik-setting',
          'items': '/event_site/billing-module/items',
          'voucher': '/event_site/billing-module/voucher',
          'purchase_policy': '/event_site/billing-module/purchase-policy',
          'orders': '/event_site/billing-module/manage-orders',
          'registration_form_stats': '/event_site/billing-module/registration_form/statsdashboard',

          'waiting_list_orders': '/event_site/billing-module/waiting-list-orders'
        };

        const route = childRouteMap[potentialNextChild.alias];
        if (route) {
          return route;
        }
        break;
      } else {
        nextChildIndex++;
      }
    }

    // If no unlocked child found within items_paid, look for next available parent menu
    const availableParents = menuHierarchy
      .filter(menu => menu.status === 1)
      .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

    const currentParentIndex = availableParents.findIndex(menu => menu.alias === 'items_paid');

    if (currentParentIndex >= 0 && currentParentIndex < availableParents.length - 1) {
      const nextParent = availableParents[currentParentIndex + 1];
      // Map parent alias to route
      const routeMap = {
        'registration': '/event/registration/basic-detail-form',
        'online_registration_form': '/event/registration/basic-detail-form',
        'event_details': '/event/edit/' + (localStorage.getItem('event_id') || ''),
        'branding': '/event/settings/branding',
        'registration_list': '/event_site/billing-module/manage-orders',
        'settings': '/event/settings/branding',
        'manage': '/event/manage/surveys',
        'survey': '/event/manage/surveys',
        'module': '/event/module/event-module-order',
        'upload_content': '/event/module/event-module-order',
        'items_paid': '/event_site/billing-module/payment-methods',
        'event_site': '/event_site/billing-module/payment-methods'
      };

      const route = routeMap[nextParent.alias];
      if (route) {
        return route;
      }
    }

    return null;

  } catch (error) {
    console.error('Items Paid Navigation Debug - Error getting next unlocked child:', error);
    return null;
  }
};

/**
 * Helper function to get lock reason for items_paid children
 * @param {string} childAlias - The alias of the child
 * @param {Object} event - The event object
 * @returns {string} - The reason for the lock status
 */
const getItemsPaidLockReason = (childAlias, event) => {
  if (childAlias === 'payment_providers') {
    const status = event.eventsite_secion_fields &&
      event.eventsite_secion_fields.company_detail.credit_card_payment.status;
    return `credit_card_payment.status = ${status}`;
  } else if (childAlias === 'ean_invoice' || childAlias === 'company_public_payment' || childAlias === 'public') {
    const status = event.eventsite_secion_fields &&
      event.eventsite_secion_fields.company_detail.company_public_payment.status;
    return `company_public_payment.status = ${status}`;
  } else if (childAlias === 'fik_setting' || childAlias === 'fik') {
    const status = event.eventsite_payment_setting &&
      event.eventsite_payment_setting.eventsite_billing_fik;
    return `eventsite_billing_fik = ${status}`;
  }
  return 'No specific lock logic defined or menu hierarchy status is 0';
};

export const getCachedMenuHierarchy = () => {
  try {
    const eventId = localStorage.getItem('event_id');
    if (!eventId) {
      return [];
    }
    const cached = localStorage.getItem(`menuHierarchy_${eventId}`);
    if (!cached) {
      return [];
    }
    const parsed = JSON.parse(cached);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

export const resolveMenuHierarchy = (menuHierarchy) => {
  const cached = getCachedMenuHierarchy();
  if (cached.length > 0) {
    return cached;
  }
  if (Array.isArray(menuHierarchy) && menuHierarchy.length > 0) {
    return menuHierarchy;
  }
  return [];
};

const GUARDED_MENU_ALIASES = new Set([
  'attendees',
  'agendas',
  'speakers',
  'team',
  'teams',
  'directory',
  'ddirectory',
  'practical-info',
  'infobooth',
  'additional-info',
  'additional_info',
  'general-info',
  'general_info',
  'information_pages',
  'information-pages',
  'maps',
  'subregistration',
  'sub_registrations',
  'news',
  'customize_app_menus',
  'customize_website_menus',
  'attendee_fields',
  'private_address_fields',
  'attendee_types',
  'custom_fields',
  'company_fields',
  'accommodation',
  'gdpr_policy',
  'terms_conditions',
  'attendee_gdpr_logs',
  'add_attendee_to_registration',
  'survey',
  'polls',
]);

export const findMenuItemByAlias = (menuHierarchy, aliases) => {
  const wanted = (Array.isArray(aliases) ? aliases : [aliases]).filter(Boolean);
  if (!menuHierarchy || wanted.length === 0) {
    return null;
  }

  for (const item of menuHierarchy) {
    if (wanted.includes(item.alias)) {
      return item;
    }
    if (Array.isArray(item.children)) {
      const child = item.children.find((menu) => wanted.includes(menu.alias));
      if (child) {
        return child;
      }
    }
  }

  return null;
};

/**
 * get-menu status gate:
 * - status === 1 → allowed
 * - status === 0 → blocked
 * - known guarded alias missing → blocked
 * - unknown alias missing → allowed
 * - hierarchy not loaded → blocked
 */
export const isGetMenuAliasAllowed = (menuHierarchy, aliases) => {
  const hierarchy = resolveMenuHierarchy(menuHierarchy);
  const wanted = (Array.isArray(aliases) ? aliases : [aliases]).filter(Boolean);

  if (!hierarchy || hierarchy.length === 0) {
    return false;
  }

  const item = findMenuItemByAlias(hierarchy, wanted);
  if (!item) {
    const isGuarded = wanted.some((alias) => GUARDED_MENU_ALIASES.has(alias));
    return !isGuarded;
  }

  return Number(item.status) === 1;
};
 